import { DEFAULT_EYEBROW_RULE_COLOR } from './blocks/eyebrow';
import {
  DEFAULT_DIVIDER_COLOR,
  DEFAULT_FONT,
  DEFAULT_HEADING_COLOR,
  DEFAULT_LINK_COLOR,
  DEFAULT_PARAGRAPH_COLOR,
} from "./factories";
import type {
  DividerBlock,
  EmailFontFamily,
  EmailSettings,
  EyebrowBlock,
  TextBlock,
  TextVariant,
} from "./types";
import type { ButtonTheme } from "./button-theme";

export type TextTheme = Pick<
  EmailSettings,
  | 'headingColor'
  | 'paragraphColor'
  | 'linkColor'
  | 'dividerColor'
  | 'headingFontFamily'
  | 'bodyFontFamily'
>;

export type RenderTheme = TextTheme & ButtonTheme;

export function isHeadingTextVariant(variant: TextVariant): boolean {
  return variant !== 'normal';
}

export function resolveThemeFontFamily(
  theme: TextTheme,
  variant: TextVariant,
): EmailFontFamily {
  return isHeadingTextVariant(variant)
    ? theme.headingFontFamily
    : theme.bodyFontFamily;
}

export function resolveTextColor(block: TextBlock, theme: TextTheme): string {
  if (block.color != null) {
    return block.color;
  }
  return isHeadingTextVariant(block.variant)
    ? theme.headingColor
    : theme.paragraphColor;
}

export function resolveLinkColor(block: TextBlock, theme: TextTheme): string {
  return block.linkColor ?? theme.linkColor;
}

export function resolveTextFontFamily(
  block: TextBlock,
  theme: TextTheme,
): EmailFontFamily {
  return block.fontFamily ?? resolveThemeFontFamily(theme, block.variant);
}

export function isTextColorInherited(block: TextBlock): boolean {
  return block.color == null;
}

export function isLinkColorInherited(block: TextBlock): boolean {
  return block.linkColor == null;
}

export function resolveDividerColor(
  block: DividerBlock,
  theme: TextTheme,
): string {
  return block.color ?? theme.dividerColor;
}

export function isDividerColorInherited(block: DividerBlock): boolean {
  return block.color == null;
}

export function resolveEyebrowTextColor(
  block: EyebrowBlock,
  theme: TextTheme,
): string {
  return block.color ?? theme.headingColor;
}

export function resolveEyebrowRuleColor(
  block: EyebrowBlock,
  theme: TextTheme,
): string {
  return block.ruleColor ?? theme.dividerColor;
}

export function normalizeDividerBlock(
  block: DividerBlock,
  settings: EmailSettings,
): DividerBlock {
  const next = { ...block };

  if (
    next.color === DEFAULT_DIVIDER_COLOR ||
    next.color === settings.dividerColor
  ) {
    delete next.color;
  }

  return next;
}

function shouldClearTextColor(
  color: string,
  variant: TextVariant,
  settings: EmailSettings,
): boolean {
  if (isHeadingTextVariant(variant)) {
    return (
      color === DEFAULT_HEADING_COLOR ||
      color === settings.headingColor ||
      color === DEFAULT_PARAGRAPH_COLOR ||
      color === settings.paragraphColor
    );
  }
  return (
    color === DEFAULT_PARAGRAPH_COLOR ||
    color === settings.paragraphColor ||
    color === DEFAULT_HEADING_COLOR ||
    color === settings.headingColor
  );
}

export function normalizeTextBlock(
  block: TextBlock,
  settings: EmailSettings,
): TextBlock {
  const next = { ...block };

  if (next.color != null && shouldClearTextColor(next.color, next.variant, settings)) {
    delete next.color;
  }

  if (
    next.linkColor === DEFAULT_LINK_COLOR ||
    next.linkColor === settings.linkColor
  ) {
    delete next.linkColor;
  }

  const inheritedFont = resolveThemeFontFamily(settings, next.variant);
  if (next.fontFamily === inheritedFont || next.fontFamily === DEFAULT_FONT) {
    delete next.fontFamily;
  }

  return next;
}

export function normalizeEyebrowBlock(
  block: EyebrowBlock,
  settings: EmailSettings,
): EyebrowBlock {
  const next = { ...block };

  if (
    next.ruleColor != null &&
    (next.ruleColor === DEFAULT_EYEBROW_RULE_COLOR ||
      next.ruleColor === DEFAULT_DIVIDER_COLOR ||
      next.ruleColor === settings.dividerColor)
  ) {
    delete next.ruleColor;
  }

  if (
    next.color != null &&
    (next.color === settings.headingColor ||
      next.color === DEFAULT_HEADING_COLOR ||
      next.color === settings.paragraphColor ||
      next.color === DEFAULT_PARAGRAPH_COLOR)
  ) {
    delete next.color;
  }

  return next;
}
